import { ExportOutlined } from '@ant-design/icons';
import Link from 'next/link';
import styled from 'styled-components';

import hmhFrontpageImage from '../../../assets/images/bluebonetfontpage.png';
import closingBoxImage from '../../../assets/images/closingboxbluebonnet.jpeg';
import greenValleyImage from '../../../assets/images/greenvalleybluebonnet.png';
import dataGrowthImage from '../../../assets/images/growth2.png';
import intoLiteratureSlideImage from '../../../assets/images/hmhintoliteraturescr.png';
import hmhFeedbackSlideImage from '../../../assets/images/hmhintoreadingfeedback.png';
import hmhQuestionSlideImage from '../../../assets/images/hmhintoreadingquestion.png';
import phonicsImage from '../../../assets/images/phonicsbluebonnet.jpeg';
import sivellBendsImage from '../../../assets/images/sivellbendsbluebonnet.png';
import spanishImage from '../../../assets/images/spanish_bluebonnet.png';
import theme from '../../../assets/theme';
import { Button } from '../../_atoms/Button';
import MaxWidthContainer from '../../_atoms/MaxWidthContainer';

const MOBILE = '(max-width: 992px)';
const HMH_REQUEST_PATH = '/hmh-request';
const SCOPE_SEQUENCE_K5_URL =
  'https://docs.google.com/spreadsheets/d/14AJA7u-kNxoHFz9RiMBn1kSUBOFwC31gXrim-jwKe5s/edit?gid=1418225403#gid=1418225403';
const SCOPE_SEQUENCE_6_8_URL =
  'https://docs.google.com/spreadsheets/d/1B-O_0EEOrcNn1x2HarJE5c9TXvyAcNS_kY2jB3FJ_48/edit?gid=1051180380#gid=1051180380';

const HMHIntoReadingLandingContent: React.FC = () => {
  return (
    <>
      {/* ───────────────── 1. HERO ───────────────── */}
      <HeroSection>
        <HeroBlobOne aria-hidden="true" />
        <HeroBlobTwo aria-hidden="true" />
        <MaxWidthContainer>
          <HeroLayout>
            <HeroLeft>
              <HeroTitle>
                HMH is your curriculum.{' '}
                <HeroTitleAccent>
                  Curipod brings engagement, writing, and insights — K through 8.
                </HeroTitleAccent>
              </HeroTitle>
              <HeroSubtitle>
                Research-based writing practice — SCRs, ECRs, revision &amp; editing, and
                more — built for HMH Into Reading Texas (K–5) and HMH Into Literature
                Texas (6–8).
              </HeroSubtitle>
              <HeroCtaRow>
                <Link href={HMH_REQUEST_PATH} passHref>
                  <a>
                    <HeroPrimaryButton type="primary">Book a Demo</HeroPrimaryButton>
                  </a>
                </Link>
              </HeroCtaRow>
            </HeroLeft>

            <HeroRight>
              <HeroImageFrame>
                <HeroImage
                  src={hmhFrontpageImage.src}
                  alt="An HMH lesson running on a Curipod activity slide in a Texas classroom, students engaged, looking up at the screen."
                />
              </HeroImageFrame>
            </HeroRight>
          </HeroLayout>
        </MaxWidthContainer>
      </HeroSection>

      {/* ───────────── 2. STUDENTS ENGAGED — HMH LESSONS ───────────── */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <InsightRow>
            <InsightLeft>
              <SectionTitle>Students Engaged and Writing SCRs Every Week</SectionTitle>
              <InsightBulletList>
                <InsightBullet>
                  <InsightBulletBody>
                    AI-scored SCRs + ECRs, aligned to each Into Reading module and Into
                    Literature unit — matched to its genre and learning objective.
                    STAAR-aligned. No stack of papers to grade.
                  </InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Built for HMH Into Reading and Into Literature Texas. Ready to go in
                    one click. No extra minutes, no extra prep.
                  </InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Students get feedback in seconds. Not next week.
                  </InsightBulletBody>
                </InsightBullet>
              </InsightBulletList>
              <InsightCtaRow>
                <InsightCtaText>
                  Connect with our team to learn how Curipod maps into your HMH scope
                  &amp; sequence — K–5 or 6–8.
                </InsightCtaText>
                <Link href={HMH_REQUEST_PATH} passHref>
                  <a>
                    <InsightCtaButton type="primary">Book a Demo</InsightCtaButton>
                  </a>
                </Link>
                <InsightSecondaryCtaLink
                  href={SCOPE_SEQUENCE_K5_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Into Reading Scope &amp; Sequence (K–5)
                  <ExportOutlined aria-hidden="true" />
                </InsightSecondaryCtaLink>
                <InsightSecondaryCtaLink
                  href={SCOPE_SEQUENCE_6_8_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Into Literature Scope &amp; Sequence (6–8)
                  <ExportOutlined aria-hidden="true" />
                </InsightSecondaryCtaLink>
              </InsightCtaRow>
            </InsightLeft>
            <InsightRight>
              <InsightImg
                src={hmhQuestionSlideImage.src}
                alt="Curipod SCR practice slide asking students to predict, using text evidence, whether Peter will follow his mother's rules."
                loading="lazy"
              />
              <InsightCaption>
                An SCR prompt students answer right inside the lesson — make and support a
                prediction using text evidence.
              </InsightCaption>
            </InsightRight>
          </InsightRow>
        </MaxWidthContainer>
      </Section>

      {/* ───────────── 3. LANGUAGE, GRAMMAR, PHONICS PRACTICE (K-5) ───────────── */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <InsightRow $reverse>
            <InsightLeft>
              <SectionTag>K–5</SectionTag>
              <SectionTitle>Language, Grammar, and Phonics Practice (K–5)</SectionTitle>
              <InsightBulletList>
                <InsightBullet>
                  <InsightBulletBody>
                    The practice, feedback, and mastery loop young readers and writers
                    need.
                  </InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Fun, meaningful phonics practice where students want to write.
                  </InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Sentence-level grammar practice applied in context, not drilled in
                    isolation. Read like a writer, and write for a reader!
                  </InsightBulletBody>
                </InsightBullet>
              </InsightBulletList>
            </InsightLeft>
            <InsightRight>
              <InsightImg
                src={phonicsImage.src}
                alt="Curipod K-5 phonics writing activity."
                loading="lazy"
              />
            </InsightRight>
          </InsightRow>
        </MaxWidthContainer>
      </Section>

      {/* ───────────── 4. ANALYSIS, GRAMMAR, ARGUMENT WRITING (6-8) ───────────── */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <InsightRow>
            <InsightLeft>
              <SectionTag>6–8</SectionTag>
              <SectionTitle>Analysis, Grammar, and Argument Writing (6–8)</SectionTitle>
              <InsightBulletList>
                <InsightBullet>
                  <InsightBulletBody>
                    The reasoning-and-evidence practice middle schoolers need to be ready
                    for STAAR — built on Into Literature&apos;s texts.
                  </InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Literary analysis, central-idea, and argument writing where students
                    apply skills to real passages, not worksheets.
                  </InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Grammar and revision practiced in context of their own writing — read
                    like a writer, write for a reader, at every grade.
                  </InsightBulletBody>
                </InsightBullet>
              </InsightBulletList>
            </InsightLeft>
            <InsightRight>
              <InsightImg
                src={intoLiteratureSlideImage.src}
                alt="Curipod 6-8 SCR practice slide on 'The Tower Stairs,' adapted from Robert Louis Stevenson's Kidnapped, asking students to analyze how a character's responses develop the plot."
                loading="lazy"
              />
              <InsightCaption>
                Grade 6 · SCR practice — Analyze how a character&apos;s responses develop
                the plot · &lsquo;The Tower Stairs&rsquo;
              </InsightCaption>
            </InsightRight>
          </InsightRow>
        </MaxWidthContainer>
      </Section>

      {/* ───────────── 5. IMPLEMENTATION INSIGHTS ───────────── */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <InsightRow $reverse>
            <InsightLeft>
              <SectionTitle>Insights for your HMH implementation</SectionTitle>
              <InsightBulletList>
                <InsightBullet>
                  <InsightBulletBody>
                    See where mastery is — and isn&apos;t — for the most important TEKS
                    reading and writing skills, K–8.
                  </InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>Spot the gap before STAAR does.</InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Built for Texas accountability alignment: HB 1605 &amp; HB 8.
                  </InsightBulletBody>
                </InsightBullet>
              </InsightBulletList>
              <InsightCtaRow>
                <InsightCtaText>
                  Interested in seeing your district&apos;s dashboard?
                </InsightCtaText>
                <Link href={HMH_REQUEST_PATH} passHref>
                  <a>
                    <InsightCtaButton type="primary">Contact our team</InsightCtaButton>
                  </a>
                </Link>
              </InsightCtaRow>
            </InsightLeft>
            <InsightRight>
              <InsightImg
                src={dataGrowthImage.src}
                alt="District dashboard showing SCR and ECR score growth rolled up across schools and grades."
                loading="lazy"
              />
            </InsightRight>
          </InsightRow>
        </MaxWidthContainer>
      </Section>

      {/* ───────────── 6. BUILT FOR YOUR PACING GUIDE ───────────── */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <InsightRow>
            <InsightLeft>
              <SectionTitle>
                Built for Your Pacing Guide. Use as a supplement or for intervention.
              </SectionTitle>
              <InsightBulletList>
                <InsightBullet>
                  <InsightBulletBody>
                    Questions, scaffolds, and student exemplars embedded in lessons.
                  </InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Timed activities keep classes moving forward together.
                  </InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Feedback and revision cycles that used to take hours happen in
                    minutes.
                  </InsightBulletBody>
                </InsightBullet>
              </InsightBulletList>
            </InsightLeft>
            <InsightRight>
              <InsightImg
                src={hmhFeedbackSlideImage.src}
                alt="Curipod AI feedback on a student's SCR response, with a score plus Glow, Grow, and Next Time coaching."
                loading="lazy"
              />
            </InsightRight>
          </InsightRow>
        </MaxWidthContainer>
      </Section>

      {/* ───────────── 7. EMERGENT BILINGUAL SUPPORT ───────────── */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <InsightRow $reverse>
            <InsightLeft>
              <SectionTitle>Emergent Bilingual Support</SectionTitle>
              <InsightBulletList>
                <InsightBullet>
                  <InsightBulletBody>Built-in translation support.</InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>Multilingual response options.</InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Scaffolding built into each activity so every EB student participates
                    — K through 8.
                  </InsightBulletBody>
                </InsightBullet>
              </InsightBulletList>
            </InsightLeft>
            <InsightRight>
              <InsightImg
                src={spanishImage.src}
                alt="Curipod activity rendered with Spanish translation and bilingual scaffolds."
                loading="lazy"
              />
            </InsightRight>
          </InsightRow>
        </MaxWidthContainer>
      </Section>

      {/* ───────────── 8. STAAR PROOF BAND ───────────── */}
      <ProofSection>
        <MaxWidthContainer>
          <NarrowCenter>
            <ProofTitle>
              Proven STAAR gains across Texas — mapped to every module and unit in HMH
              Into Reading and Into Literature
            </ProofTitle>
            <ProofSubtitle>
              Districts already use Curipod to move STAAR scores, campus ratings, and
              passing rates. These are results your HMH implementation can build on — in
              elementary and middle school alike.
            </ProofSubtitle>
          </NarrowCenter>

          <FeaturedStoryGrid>
            <Link href="/case-studies/curipod-case-study-green-valley" passHref>
              <FeaturedStoryCard>
                <FeaturedStoryImage
                  src={greenValleyImage.src}
                  alt="Green Valley Elementary students using Curipod."
                  loading="lazy"
                />
                <FeaturedStoryBody>
                  <FeaturedStoryStat>69% → 89%</FeaturedStoryStat>
                  <FeaturedStoryTitle>
                    Green Valley&apos;s Third-Grade Growth Story.
                  </FeaturedStoryTitle>
                  <FeaturedStoryMeta>
                    Region 20 · Schertz-Cibolo-Universal City ISD · 2024–2025 School Year
                  </FeaturedStoryMeta>
                  <FeaturedStoryLink>Read the story →</FeaturedStoryLink>
                </FeaturedStoryBody>
              </FeaturedStoryCard>
            </Link>
            <Link href="/case-studies/curipod-case-study-sivells-bend" passHref>
              <FeaturedStoryCard>
                <FeaturedStoryImage
                  src={sivellBendsImage.src}
                  alt="Sivells Bend ISD classroom showcasing a Curipod activity."
                  loading="lazy"
                />
                <FeaturedStoryBody>
                  <FeaturedStoryStat>C → A</FeaturedStoryStat>
                  <FeaturedStoryTitle>
                    Sivells Bend ISD: A campus rating in one year.
                  </FeaturedStoryTitle>
                  <FeaturedStoryMeta>Sivells Bend ISD · North Texas</FeaturedStoryMeta>
                  <FeaturedStoryLink>Read the story →</FeaturedStoryLink>
                </FeaturedStoryBody>
              </FeaturedStoryCard>
            </Link>
          </FeaturedStoryGrid>

          <StatStrip>
            <Link href="/case-studies/curipod-case-study-south-texas" passHref>
              <a>
                <StatCard>
                  <StatBigNumber>
                    +22<StatBigPct>%</StatBigPct>
                  </StatBigNumber>
                  <StatCardLabel>Meets &amp; Masters on STAAR</StatCardLabel>
                  <StatCardSub>South Texas</StatCardSub>
                </StatCard>
              </a>
            </Link>
            <Link href="/case-studies/curipod-case-study-south-east-texas" passHref>
              <a>
                <StatCard>
                  <StatBigNumber>
                    +32<StatBigPct>%</StatBigPct>
                  </StatBigNumber>
                  <StatCardLabel>Growth in STAAR scores</StatCardLabel>
                  <StatCardSub>South East Texas</StatCardSub>
                </StatCard>
              </a>
            </Link>
            <Link href="/case-studies/curipod-case-study-flour-bluff" passHref>
              <a>
                <StatCard>
                  <StatBigNumber>
                    +43<StatBigPct>%</StatBigPct>
                  </StatBigNumber>
                  <StatCardLabel>A 4th-grade transformation</StatCardLabel>
                  <StatCardSub>Corpus Christi, TX</StatCardSub>
                </StatCard>
              </a>
            </Link>
            <Link href="/case-studies/curipod-case-study-green-valley" passHref>
              <a>
                <StatCard>
                  <StatBigNumber>
                    +20<StatBigPct>%</StatBigPct>
                  </StatBigNumber>
                  <StatCardLabel>Increase in STAAR RLA Passing Rate</StatCardLabel>
                  <StatCardSub>Schertz-Cibolo-Universal City ISD</StatCardSub>
                </StatCard>
              </a>
            </Link>
          </StatStrip>

          <ProofAllStoriesLinkRow>
            <Link href="/case-studies" passHref>
              <ProofAllStoriesLink>See all the case studies →</ProofAllStoriesLink>
            </Link>
          </ProofAllStoriesLinkRow>
        </MaxWidthContainer>
      </ProofSection>

      {/* ───────────── 9. CLOSING CTA ───────────── */}
      <FinalCtaWrapper>
        <MaxWidthContainer>
          <FinalCtaCard>
            <FinalCtaImageWrap>
              <FinalCtaImage
                src={closingBoxImage.src}
                alt="Curipod team working with Texas literacy educators."
                loading="lazy"
              />
            </FinalCtaImageWrap>
            <FinalCtaContent>
              <FinalCtaBody>
                Meet with us to learn how we can support your HMH Into Reading and Into
                Literature implementation.
              </FinalCtaBody>
              <Link href={HMH_REQUEST_PATH} passHref>
                <a>
                  <FinalCtaPrimaryButton type="primary">
                    Book a Call
                  </FinalCtaPrimaryButton>
                </a>
              </Link>
            </FinalCtaContent>
          </FinalCtaCard>
        </MaxWidthContainer>
      </FinalCtaWrapper>
    </>
  );
};

export default HMHIntoReadingLandingContent;

/* ─── 1. HERO ─────────────────────────────────────────────────────────────── */

const HeroSection = styled.section`
  position: relative;
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  color: ${theme.colors.white};
  padding: 100px 20px 90px;
  overflow: hidden;

  @media ${MOBILE} {
    padding: 80px 16px 64px;
  }
`;

const HeroBlobOne = styled.div`
  position: absolute;
  top: -180px;
  right: -140px;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  pointer-events: none;
`;

const HeroBlobTwo = styled.div`
  position: absolute;
  bottom: -120px;
  left: -100px;
  width: 460px;
  height: 460px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.07);
  pointer-events: none;
`;

const HeroLayout = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  gap: 56px;
  align-items: center;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 36px;
  }
`;

const HeroLeft = styled.div`
  position: relative;
`;

const HeroRight = styled.div`
  position: relative;

  @media ${MOBILE} {
    order: -1;
  }
`;

const HeroImageFrame = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.25), 0 0 0 6px rgba(255, 255, 255, 0.18);
  background: ${theme.colors.white};
  aspect-ratio: 4 / 3;

  @media ${MOBILE} {
    aspect-ratio: 3 / 2;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const HeroTitle = styled.h1`
  font-size: 54px;
  line-height: 1.08;
  font-weight: 900;
  color: ${theme.colors.white};
  margin: 0 0 20px;
  letter-spacing: -1.2px;

  @media (max-width: 1200px) {
    font-size: 46px;
  }

  @media ${MOBILE} {
    font-size: 38px;
  }

  @media (max-width: 420px) {
    font-size: 30px;
  }
`;

const HeroTitleAccent = styled.span`
  display: block;
  font-weight: 800;
  color: #fff3e0;
  letter-spacing: -0.8px;
  margin-top: 6px;
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.92);
  max-width: 640px;
  line-height: 1.65;
  margin: 0 0 32px;

  @media ${MOBILE} {
    font-size: 16px;
  }
`;

const HeroCtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px;
`;

const HeroPrimaryButton = styled(Button)`
  background-color: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  border-radius: 28px;
  padding: 0 36px;
  font-size: 17px;
  height: 56px;
  font-weight: 800;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18), 0 0 0 4px rgba(255, 255, 255, 0.22);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.22), 0 0 0 5px rgba(255, 255, 255, 0.32);
    background-color: ${theme.colors.white};
    color: ${theme.colors.orangeDark};
  }
`;

/* ─── SHARED SECTION PRIMITIVES ────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 90px 0;

  @media ${MOBILE} {
    padding: 56px 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  font-weight: 800;
  color: ${theme.colors.black};
  margin: 0 0 22px;
  line-height: 1.15;
  letter-spacing: -0.6px;

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const NarrowCenter = styled.div`
  max-width: 820px;
  margin: 0 auto 56px;
  text-align: left;

  @media ${MOBILE} {
    margin-bottom: 36px;
  }
`;

const SectionTag = styled.span`
  display: inline-block;
  background: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 5px 14px;
  border-radius: 999px;
  border: 1.5px solid ${theme.colors.orange};
  margin-bottom: 14px;
`;

/* ─── INSIGHT ROWS ───────────────────────────────────────────────────────── */

const InsightRow = styled.div<{ $reverse?: boolean }>`
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: 56px;
  align-items: center;
  direction: ${({ $reverse }) => ($reverse ? 'rtl' : 'ltr')};

  & > * {
    direction: ltr;
  }

  @media ${MOBILE} {
    direction: ltr;
    grid-template-columns: 1fr;
    gap: 36px;

    /* On mobile always stack text first, image second. */
    & > :first-child {
      order: 1;
    }
    & > :nth-child(2) {
      order: 2;
    }
  }
`;

const InsightCaption = styled.div`
  margin-top: 14px;
  font-size: 13px;
  font-weight: 700;
  font-style: italic;
  color: ${theme.colors.text.dark};
  line-height: 1.45;
`;

const InsightCtaRow = styled.div`
  margin-top: 28px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 18px;
`;

const InsightCtaText = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: ${theme.colors.black};
  margin: 0;
  flex: 1 1 auto;
  line-height: 1.5;
`;

const InsightCtaButton = styled(Button)`
  background-color: ${theme.colors.orange};
  color: ${theme.colors.white};
  border-radius: 28px;
  padding: 0 26px;
  font-size: 14px;
  height: 44px;
  font-weight: 800;
  white-space: nowrap;

  &:hover {
    background-color: ${theme.colors.orangeDark};
    color: ${theme.colors.white};
  }
`;

const InsightSecondaryCtaLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 2px solid ${theme.colors.orange};
  color: ${theme.colors.orangeDark};
  background: transparent;
  border-radius: 28px;
  padding: 0 22px;
  height: 44px;
  font-size: 14px;
  font-weight: 800;
  white-space: nowrap;
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${theme.colors.orange};
    color: ${theme.colors.white};
  }

  svg {
    width: 14px;
    height: 14px;
  }

  @media (max-width: 420px) {
    white-space: normal;
    height: auto;
    padding: 10px 22px;
    text-align: center;
  }
`;

const InsightLeft = styled.div``;

const InsightRight = styled.div``;

const InsightImgFrame = styled.div`
  background: rgb(250, 243, 234);
  padding: 20px;
  border-radius: 16px;
  border: 1px solid rgba(232, 123, 39, 0.18);
  box-shadow: 0 16px 38px rgba(26, 21, 48, 0.08);
`;

const InsightImgInner = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 10px;
  object-fit: contain;
`;

const InsightImg: React.FC<React.ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  alt: string;
}> = props => (
  <InsightImgFrame>
    <InsightImgInner {...props} />
  </InsightImgFrame>
);

const InsightBulletList = styled.ul`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InsightBullet = styled.li`
  position: relative;
  padding-left: 28px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 9px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${theme.colors.orange};
  }
`;

const InsightBulletBody = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: ${theme.colors.black};
  line-height: 1.55;
  margin: 0;
`;

/* ─── STAAR PROOF BAND ───────────────────────────────────────────────────── */

const ProofSection = styled.section`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  padding: 100px 0;
  color: ${theme.colors.white};

  @media ${MOBILE} {
    padding: 64px 0;
  }
`;

const ProofTitle = styled(SectionTitle)`
  color: ${theme.colors.white};
  margin-bottom: 16px;

  @media (max-width: 1200px) {
    font-size: 36px;
  }
`;

const ProofSubtitle = styled.p`
  font-size: 17px;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.6;
  max-width: 760px;
  margin: 0 0 48px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const FeaturedStoryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
  margin: 0 auto 36px;
  max-width: 880px;
  justify-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    max-width: 460px;
  }

  a {
    text-decoration: none;
    color: inherit;
    display: block;
  }
`;

const FeaturedStoryCard = styled.a`
  display: block;
  background: ${theme.colors.white};
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 14px;
  overflow: hidden;
  color: ${theme.colors.black};
  text-decoration: none;
  box-shadow: 0 18px 38px rgba(26, 21, 48, 0.18);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 22px 48px rgba(26, 21, 48, 0.24);
  }
`;

const FeaturedStoryImage = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  background: ${theme.colors.paleBeige};
`;

const FeaturedStoryBody = styled.div`
  padding: 26px 28px 28px;
`;

const FeaturedStoryStat = styled.div`
  font-size: 38px;
  font-weight: 900;
  color: ${theme.colors.orangeDark};
  line-height: 1;
  letter-spacing: -1px;
  margin-bottom: 12px;
`;

const FeaturedStoryTitle = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: ${theme.colors.black};
  margin-bottom: 10px;
  line-height: 1.3;
`;

const FeaturedStoryMeta = styled.div`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  letter-spacing: 0.4px;
  margin-bottom: 18px;
  line-height: 1.5;
`;

const FeaturedStoryLink = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: ${theme.colors.orangeDark};
  text-transform: uppercase;
  letter-spacing: 1.2px;
`;

const StatStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  max-width: 880px;
  margin: 0 auto;
  justify-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    max-width: 520px;
  }

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
    max-width: 320px;
  }

  a {
    text-decoration: none;
    color: inherit;
    display: block;
  }
`;

const StatCard = styled.div`
  padding: 24px 14px;
  border-radius: 10px;
  text-align: center;
  height: 100%;
  width: 100%;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.32);

  &:hover {
    transform: translateY(-4px);
    background: rgba(255, 255, 255, 0.26);
  }
`;

const StatBigNumber = styled.div`
  font-size: 40px;
  font-weight: 900;
  line-height: 1;
  color: ${theme.colors.white};
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
  letter-spacing: -1px;

  @media ${MOBILE} {
    font-size: 34px;
  }
`;

const StatBigPct = styled.span`
  font-size: 22px;
  font-weight: 800;
`;

const StatCardLabel = styled.div`
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: ${theme.colors.white};
  margin-top: 12px;
  line-height: 1.35;
`;

const StatCardSub = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 4px;
`;

const ProofAllStoriesLinkRow = styled.div`
  margin-top: 36px;
  text-align: center;
`;

const ProofAllStoriesLink = styled.a`
  display: inline-block;
  color: ${theme.colors.white};
  font-weight: 800;
  font-size: 15px;
  text-decoration: none;
  border-bottom: 2px solid rgba(255, 255, 255, 0.55);
  padding-bottom: 4px;
  transition: border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    border-bottom-color: ${theme.colors.white};
    transform: translateX(2px);
  }
`;

/* ─── CLOSING CTA ────────────────────────────────────────────────────────── */

const FinalCtaWrapper = styled.section`
  background: ${theme.colors.background};
  padding: 64px 0 80px;

  @media ${MOBILE} {
    padding: 48px 0 56px;
  }
`;

const FinalCtaCard = styled.div`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  border-radius: 20px;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  align-items: stretch;
  color: ${theme.colors.white};
  box-shadow: 0 24px 50px rgba(232, 123, 39, 0.25);
  overflow: hidden;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const FinalCtaImageWrap = styled.div`
  position: relative;
  min-height: 280px;
  background: ${theme.colors.paleBeige};
`;

const FinalCtaImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  min-height: 280px;
  object-fit: cover;
`;

const FinalCtaContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
  padding: 48px 56px;

  @media ${MOBILE} {
    padding: 32px 28px 36px;
    gap: 20px;
  }
`;

const FinalCtaBody = styled.p`
  font-size: 24px;
  font-weight: 800;
  color: ${theme.colors.white};
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.3px;

  @media ${MOBILE} {
    font-size: 20px;
  }
`;

const FinalCtaPrimaryButton = styled(Button)`
  background-color: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  border-radius: 28px;
  padding: 0 32px;
  font-size: 16px;
  height: 50px;
  font-weight: 800;
  align-self: flex-start;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    background-color: ${theme.colors.white};
    color: ${theme.colors.orangeDark};
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.22);
  }
`;
